/**
 * The cell grid behind packing and carving (TD §7.2, §7.3): GRID_N × GRID_N square cells over the map square.
 * Cell k = j*N + i covers [i*c, (i+1)*c] × [j*c, (j+1)*c]; its sample point is the cell centre.
 */
import type { Pt } from '../../shared/types';
import { bbox, clamp, distSeg, pip, type ReadPt } from '../geom';

/** Cells per side, as in the prototype. */
export const GRID_N = 200;

/** Cell classes used by packing. */
export const CLS_OUT = 0;
export const CLS_LAND = 1;
export const CLS_ROAD = 2;
export const CLS_WATER = 3;
export const CLS_RESERVED = 4;

/** A square cell grid over the map: N cells per side, each c metres wide. */
export interface Grid {
  N: number;
  /** Cell size in metres. */
  c: number;
  /** Cell count, N*N. */
  n: number;
}

/** Grid over a map of `size` metres. */
export function makeGrid(size: number, N = GRID_N): Grid {
  return { N, c: size / N, n: N * N };
}

/** Centre coordinate (metres) of cell column or row i. */
export function cellMid(g: Grid, i: number): number {
  return (i + 0.5) * g.c;
}

/** Cell index range [i0, j0, i1, j1] (inclusive) covering a box in metres. */
export function cellRange(g: Grid, x0: number, y0: number, x1: number, y1: number): [number, number, number, number] {
  const m = g.N - 1;
  return [clamp(Math.floor(x0 / g.c), 0, m), clamp(Math.floor(y0 / g.c), 0, m), clamp(Math.ceil(x1 / g.c), 0, m), clamp(Math.ceil(y1 / g.c), 0, m)];
}

/** Calls fn for every cell whose centre lies inside the polygon. */
export function forCellsInPoly(g: Grid, poly: readonly ReadPt[], fn: (k: number) => void): void {
  if (poly.length < 3) return;
  const [x0, y0, x1, y1] = bbox(poly), [i0, j0, i1, j1] = cellRange(g, x0, y0, x1, y1);
  for (let j = j0; j <= j1; j++) {
    const y = cellMid(g, j);
    for (let i = i0; i <= i1; i++) if (pip(poly, cellMid(g, i), y)) fn(j * g.N + i);
  }
}

/**
 * Calls fn for every cell whose centre lies within hw(s) of segment s of the polyline (once per segment hit,
 * so a cell can be reported more than once).
 */
export function forCellsNearLine(g: Grid, poly: readonly ReadPt[], hw: (s: number) => number, fn: (k: number, s: number) => void): void {
  for (let s = 0; s < poly.length - 1; s++) {
    const a = poly[s] as ReadPt, b = poly[s + 1] as ReadPt, h = hw(s);
    const [i0, j0, i1, j1] = cellRange(g, Math.min(a[0], b[0]) - h, Math.min(a[1], b[1]) - h, Math.max(a[0], b[0]) + h, Math.max(a[1], b[1]) + h);
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      if (distSeg(cellMid(g, i), cellMid(g, j), a, b) <= h) fn(j * g.N + i, s);
    }
  }
}

/** The 4-neighbours of cell k, written into `out`; returns how many there are. */
export function nbrs4(g: Grid, k: number, out: Int32Array): number {
  const N = g.N, i = k % N;
  let m = 0;
  if (i > 0) out[m++] = k - 1;
  if (i < N - 1) out[m++] = k + 1;
  if (k >= N) out[m++] = k - N;
  if (k < g.n - N) out[m++] = k + N;
  return m;
}

/**
 * Keeps only the largest 4-connected piece of every label (or of the labels in `only`), clearing the rest to -1.
 * With `prefer`, a piece containing a preferred cell beats any piece without one, as long as it is big enough to keep.
 * Labels whose kept piece is smaller than `minCells` (a number, or one per label) are cleared completely. Returns the
 * kept size per label.
 */
export function keepLargestPieces(g: Grid, lab: Int32Array, minCells: number | ((label: number) => number), only?: ReadonlySet<number>, prefer?: Uint8Array): Map<number, number> {
  const minOf = typeof minCells === 'number' ? () => minCells : minCells;
  const seen = new Uint8Array(g.n), best = new Map<number, number[]>(), bestScore = new Map<number, number>(), nb = new Int32Array(4);
  for (let k0 = 0; k0 < g.n; k0++) {
    const L = lab[k0] as number;
    if (L < 0 || seen[k0] || (only && !only.has(L))) continue;
    const q = [k0];
    seen[k0] = 1;
    let pref = false;
    for (let a = 0; a < q.length; a++) {
      const c = q[a] as number, m = nbrs4(g, c, nb);
      if (prefer && prefer[c]) pref = true;
      for (let t = 0; t < m; t++) { const k = nb[t] as number; if (lab[k] === L && !seen[k]) { seen[k] = 1; q.push(k); } }
    }
    const cur = best.get(L), score = q.length + (pref && q.length >= minOf(L) ? g.n : 0);
    if (!cur || score > (bestScore.get(L) as number)) { if (cur) for (const k of cur) lab[k] = -1; best.set(L, q); bestScore.set(L, score); }
    else for (const k of q) lab[k] = -1;
  }
  const out = new Map<number, number>();
  for (const [L, cells] of best) {
    if (cells.length < minOf(L)) { for (const k of cells) lab[k] = -1; continue; }
    out.set(L, cells.length);
  }
  return out;
}

/**
 * Hands every labelled cell that sits in a hole of another label to the enclosing label, so no area's outer ring
 * can contain another area. Labels are 4-connected; the background is 8-connected (standard digital topology).
 */
export function absorbHoles(g: Grid, lab: Int32Array): void {
  const N = g.N, box = new Map<number, [number, number, number, number]>();
  for (let k = 0; k < g.n; k++) {
    const L = lab[k] as number;
    if (L < 0) continue;
    const i = k % N, j = (k - i) / N, b = box.get(L);
    if (!b) box.set(L, [i, j, i, j]);
    else { if (i < b[0]) b[0] = i; if (j < b[1]) b[1] = j; if (i > b[2]) b[2] = i; if (j > b[3]) b[3] = j; }
  }
  for (const [L, [i0, j0, i1, j1]] of box) {
    // local window with a one-cell margin that counts as outside
    const w = i1 - i0 + 3, h = j1 - j0 + 3, out = new Uint8Array(w * h), q: number[] = [];
    const isL = (li: number, lj: number) => {
      const i = li + i0 - 1, j = lj + j0 - 1;
      return i >= 0 && j >= 0 && i < N && j < N && lab[j * N + i] === L;
    };
    for (let lj = 0; lj < h; lj++) for (let li = 0; li < w; li++) {
      if ((li === 0 || lj === 0 || li === w - 1 || lj === h - 1) && !isL(li, lj)) { out[lj * w + li] = 1; q.push(lj * w + li); }
    }
    for (let a = 0; a < q.length; a++) {
      const p = q[a] as number, li = p % w, lj = (p - li) / w;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        const ni = li + di, nj = lj + dj;
        if (ni < 0 || nj < 0 || ni >= w || nj >= h) continue;
        const np = nj * w + ni;
        if (!out[np] && !isL(ni, nj)) { out[np] = 1; q.push(np); }
      }
    }
    for (let lj = 1; lj < h - 1; lj++) for (let li = 1; li < w - 1; li++) {
      if (out[lj * w + li] || isL(li, lj)) continue;
      const k = (lj + j0 - 1) * N + (li + i0 - 1);
      if ((lab[k] as number) >= 0) lab[k] = L;
    }
  }
}

/** Cell-corner grid coordinates to metres. */
export function cornerToMetres(g: Grid, p: readonly [number, number]): Pt {
  return [p[0] * g.c, p[1] * g.c];
}
